import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Autocomplete,
  Box
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { salesService, creditNoteService } from '../../services';

/**
 * Credit Note Details Component
 * 
 * @param {Object} props
 * @param {Object} props.creditNote - Credit note data
 * @param {Array} props.salesInvoices - List of sales invoices
 * @param {Object} props.selectedInvoice - Currently selected invoice
 * @param {Object} props.controlStyle - Style object for form controls
 * @param {Function} props.onCreditNoteChange - Function to handle credit note changes
 * @param {Function} props.onDateChange - Function to handle date changes
 * @param {Function} props.onInvoiceSelect - Function to handle invoice selection
 */
const CreditNoteDetails = ({
  creditNote,
  salesInvoices,
  selectedInvoice,
  controlStyle,
  onCreditNoteChange,
  onDateChange,
  onInvoiceSelect
}) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch next credit note number when component mounts
  useEffect(() => {
    // Only fetch if we don't already have a credit note number
    if (!creditNote.creditNoteNumber) {
      fetchNextCreditNoteNumber();
    }
  }, []);

  // Fetch invoice details when an invoice is selected
  useEffect(() => {
    // Check if we have a selected invoice with invoiceNo property
    if (selectedInvoice && selectedInvoice.invoiceNo) {
      // Only update the originalInvoice field, let NewCreditNote handle the customer data
      const updatedCreditNote = {
        ...creditNote,
        originalInvoice: selectedInvoice.invoiceNo
      };
      
      // Log that we're updating only the originalInvoice field
      console.log('CreditNoteDetails: Updating originalInvoice field:', selectedInvoice.invoiceNo);
      
      onCreditNoteChange(updatedCreditNote);
    }
  }, [selectedInvoice]);

  // Function to process invoice data that already has complete information
  const processInvoiceData = (invoiceData) => {
    try {
      const { master, details } = invoiceData;
      
      if (master && details) {
        
        // Create a complete invoice object with the data
        const completeInvoice = {
          invoiceNo: master.invoiceNo,
          invoiceDate: master.invoiceDate,
          customerName: master.customerName,
          customerCode: master.customerCode,
          vatNumber: master.vat,
          customerAddress: master.customerAddress,
          customerPhone: master.customerPhone,
          items: details.map(item => ({
            itemCode: item.itemCode,
            description: item.description,
            quantity: item.qty,
            price: item.unitPrice,
            discount: item.discountPerc,
            vatRate: item.vat,
            total: item.grandTotal
          }))
        };
        
        
        onInvoiceSelect(null, completeInvoice);
        
        // Also update the credit note with customer information
        const updatedCreditNote = {
          ...creditNote,
          customer: {
            name: master.customerName,
            code: master.customerCode,
            vatNumber: master.vat,
            streetName: master.customerAddress,
            phone: master.customerPhone
          }
        };
        onCreditNoteChange(updatedCreditNote);
      } else {
        console.warn('Missing master or details data in invoice');
      }
    } catch (error) {
      console.error('Error processing invoice data:', error);
    }
  };

  // Function to fetch invoice details from the API
  const fetchInvoiceDetails = async (invoiceNumber) => {
    try {
      
      // Use the salesService instead of direct axios call
      const response = await salesService.getSalesInvoiceByInvoiceNo(invoiceNumber);

      if (response && response.success) {
        const { master, details } = response.data;
        
        // Create a complete invoice object with the data from the API
        const completeInvoice = {
          ...selectedInvoice,
          invoiceNo: master.invoiceNo,
          invoiceDate: master.invoiceDate,
          customerName: master.customerName,
          customerCode: master.customerCode,
          vatNumber: master.vat,
          customerAddress: master.customerAddress,
          customerPhone: master.customerPhone,
          items: details.map(item => ({
            itemCode: item.itemCode,
            description: item.description,
            quantity: item.qty,
            price: item.unitPrice,
            discount: item.discountPerc,
            vatRate: item.vat,
            total: item.grandTotal
          }))
        };
        
        onInvoiceSelect(null, completeInvoice);
        
        // Also update the credit note with customer information
        const updatedCreditNote = {
          ...creditNote,
          customer: {
            name: master.customerName,
            code: master.customerCode,
            vatNumber: master.vat,
            streetName: master.customerAddress,
            phone: master.customerPhone
          }
        };
        onCreditNoteChange(updatedCreditNote);
      } else {
        console.warn('API request failed or returned no data:', response.data);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  // Function to fetch the next credit note number from the API
  const fetchNextCreditNoteNumber = async () => {
    setIsLoading(true);
    try {
      // Use the creditNoteService instead of direct axios call
      const response = await creditNoteService.getNextCreditNoteNumber();

      if (response && response.success) {
        // Update the credit note with the new number, but preserve the customer data
        console.log('Fetched next credit note number:', response.nextInvoiceNumber);
        console.log('Current creditNote state before update:', creditNote);
        
        // Only update the creditNoteNumber field, preserve all other fields
        onCreditNoteChange(prev => ({
          ...prev,
          creditNoteNumber: response.nextInvoiceNumber
        }));
      }
    } catch (error) {
      console.error('Error fetching next credit note number:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle reason change
  const handleReasonChange = (e) => {
    const newReason = e.target.value;
    onCreditNoteChange(prev => ({
      ...prev,
      reasonForCreditNote: newReason
    }));
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        Credit Note Details
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Ref Invoice Number"
            value={selectedInvoice ? selectedInvoice.invoiceNo || selectedInvoice.number || '' : ''}
            InputProps={{
              readOnly: true,
            }}
            required
            fullWidth
            margin="dense"
            size="small"
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Credit Note Number"
            value={isLoading ? 'Loading...' : (creditNote.creditNoteNumber || '')}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Ref Invoice Date"
              value={selectedInvoice ? 
                (selectedInvoice.invoiceDate ? new Date(selectedInvoice.invoiceDate) : 
                 selectedInvoice.date ? new Date(selectedInvoice.date) : null) : 
                null
              }
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={creditNote.date}
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reason for Credit Note"
            value={creditNote.reasonForCreditNote || ''}
            onChange={handleReasonChange}
            fullWidth
            multiline
            rows={2.5}
            margin="dense"
            size="small"
            required
            error={!creditNote.reasonForCreditNote}
            helperText={!creditNote.reasonForCreditNote ? 'Reason is required' : ''}
            sx={{
              ...controlStyle,
              '& .MuiOutlinedInput-root': {
                minHeight: 90,
                borderRadius: 0
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CreditNoteDetails;
